"use client";

import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Small clipboard copy button — shows a checkmark for 1.5 s after copying.
 *
 * @param root0 - Props: `text` to copy, optional `label`, `className`,
 * and `iconSize` for UI customization.
 * @returns Copy button with transient "copied" state feedback.
 */
export function CopyButton({
  text,
  label = "Copy",
  className,
  iconSize = 11,
}: {
  text: string;
  label?: string;
  className?: string;
  iconSize?: number;
}): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1500);
      })
      .catch(() => undefined);
  };

  return (
    <button
      type="button"
      title={label}
      onClick={handleCopy}
      className={cn("flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium text-muted-foreground hover:text-foreground border border-border hover:border-foreground/30 transition-colors", className)}
    >
      {copied ? (
        <IconCheck size={iconSize} className="text-green-500" />
      ) : (
        <IconCopy size={iconSize} />
      )}
      {copied ? "Copied" : label}
    </button>
  );
}
